import { ColumnDef } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { DragDropChangeValue } from "components";
import { getLinkToWithNewSearchParameter, moveElementInArray } from "helpers";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DateTableCell,
  ItemsTableDragDrop,
  RowDragHandleCell,
  Status,
  TableItemActionsControl,
  TableItemStatusControl
} from "ui";

import { useProductSetsReorder, useUpdateProductSet } from "../../apis";
import { SETS_SHOW_ON_STATUSES } from "../../constants";
import { ProductSetsListItem } from "../../productsSets.types";
import { CoverMediaCell } from "./CoverMediaCell";
import { ProductSetsTableProps } from "./ProductSetsTable.types";

import styles from "./ProductSetsTable.module.scss";

const ProductSetsTable = ({
  productSetsList,
  sorting,
  setSorting,
  isLoading,
  onDeleteProductSet,
  onEditProductSet,
  isDragDisabled,
  setProductSets
}: ProductSetsTableProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  const productSetsReorder = useProductSetsReorder();

  const updateProductSet = useUpdateProductSet({
    onSuccess: () => {
      setOpenedItemShowOnHomeIndex(null);
      setOpenedItemShowOnSearchIndex(null);
    }
  });

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);
  const [openedItemShowOnHomeIndex, setOpenedItemShowOnHomeIndex] = useState<
    number | null
  >(null);
  const [openedItemShowOnSearchIndex, setOpenedItemShowOnSearchIndex] =
    useState<number | null>(null);

  const showOnHomeStatuses: Status[] = SETS_SHOW_ON_STATUSES.map(
    (orStatus) => ({
      ...orStatus,
      onClick: (selectedValue) => {
        if (openedItemShowOnHomeIndex === null) return;

        updateProductSet.mutate({
          id: productSetsList[openedItemShowOnHomeIndex].id,
          showOnHome: !!selectedValue
        });
      }
    })
  );

  const showOnSearchStatuses: Status[] = SETS_SHOW_ON_STATUSES.map(
    (orStatus) => ({
      ...orStatus,
      onClick: (selectedValue) => {
        if (openedItemShowOnSearchIndex === null) return;

        updateProductSet.mutate({
          id: productSetsList[openedItemShowOnSearchIndex].id,
          showInSearch: !!selectedValue
        });
      }
    })
  );

  const onChangeBannerPriority = (value: DragDropChangeValue) => {
    const { draggableId, toKeyForReorder, fromIndex, toIndex } = value;

    setProductSets(
      moveElementInArray({
        array: productSetsList,
        keyForReorder: "order",
        fromIndex,
        toIndex
      })
    );

    productSetsReorder.mutate({
      id: draggableId,
      newOrder: toKeyForReorder !== undefined ? toKeyForReorder : toIndex
    });
  };

  const columns: Array<ColumnDef<ProductSetsListItem, ProductSetsListItem[]>> =
    useMemo(
      () => [
        {
          id: "dragHandle",
          header: "",
          enableSorting: false,
          size: 3,
          minSize: 3,
          cell: (cell) => (
            <RowDragHandleCell
              rowId={cell.row.id}
              isDragDisabled={isDragDisabled}
            />
          )
        },
        {
          id: "Banner",
          header: t("Banner"),
          size: 7,
          minSize: 7,
          cell: CoverMediaCell
        },
        {
          id: "Title",
          accessorKey: "Title",
          header: t("Title"),
          size: 13,
          minSize: 13,
          cell: ({ row }) => (
            <div className={styles.productSetsTable_title}>
              {row.original.title}
            </div>
          )
        },
        {
          id: "Description",
          accessorKey: "Description",
          header: t("Description"),
          size: 25,
          minSize: 25,
          cell: ({ row }) => (
            <span className={styles.productSetsTable_description}>
              {row.original.description}
            </span>
          )
        },
        {
          header: t("Products"),
          size: 7,
          minSize: 7,
          cell: ({ row }) => row.original.numberOfProducts
        },
        {
          id: "ShowOnHome",
          accessorKey: "ShowOnHome",
          header: t("Show on Home"),
          size: 10,
          minSize: 10,
          cell: ({ row }) => (
            <TableItemStatusControl
              rowIndex={row.index}
              activeStatusIndex={row.original.showOnHome ? 0 : 1}
              openedItemStatusesIndex={openedItemShowOnHomeIndex}
              setOpenedItemStatusesIndex={setOpenedItemShowOnHomeIndex}
              statusesList={showOnHomeStatuses}
            />
          )
        },
        {
          id: "ShowInSearch",
          accessorKey: "ShowInSearch",
          header: t("Show in Search"),
          size: 10,
          minSize: 10,
          cell: ({ row }) => (
            <TableItemStatusControl
              rowIndex={row.index}
              activeStatusIndex={row.original.showInSearch ? 0 : 1}
              openedItemStatusesIndex={openedItemShowOnSearchIndex}
              setOpenedItemStatusesIndex={setOpenedItemShowOnSearchIndex}
              statusesList={showOnSearchStatuses}
            />
          )
        },
        {
          id: "CreatedAt",
          accessorKey: "CreatedAt",
          header: t("Date Created"),
          size: 10,
          minSize: 10,
          cell: ({ row }) => <DateTableCell date={row.original.createdAt} />
        },
        {
          accessorKey: "dotsColumn",
          header: () => null,
          size: 3,
          minSize: 3,
          cell: ({ row }) => (
            <TableItemActionsControl
              actionsList={[
                {
                  label: "Edit",
                  Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
                  onClick: () => onEditProductSet(row.original.id)
                },
                {
                  label: "Delete",
                  Icon: (
                    <TrashAltIcon fill={"#ff0033"} width={18} height={18} />
                  ),
                  labelColor: "#ff0033",
                  onClick: () => onDeleteProductSet(row.original.id)
                }
              ]}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          ),
          enableSorting: false
        }
      ],
      [
        isDragDisabled,
        openedItemOptionsIndex,
        openedItemShowOnHomeIndex,
        openedItemShowOnSearchIndex,
        currentLanguage
      ]
    );

  return (
    <ItemsTableDragDrop
      className={styles.productSetsTable}
      columns={columns}
      data={productSetsList}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={65}
      droppableId={"productSetsList"}
      keyForReorder={"order"}
      onChange={onChangeBannerPriority}
      onRowClick={({ id }) => {
        navigate({
          search: getLinkToWithNewSearchParameter({
            searchParams,
            parameterName: "set_id",
            parameterValue: id
          }).search
        });
      }}
      isDragDisabled={isDragDisabled}
    />
  );
};

export default ProductSetsTable;
